import { UseQueryOptions } from 'react-query'

import { baseAPIPathGuruKS } from 'configs/api'

import { useInfiniteQueryApi, useMutationApi, useQueryApi } from 'utils/api'

import { CandidateDetail, CandidateType } from 'types/candidate'
import { RegionType } from 'types/region-type'
import {
  ActiveSelection,
  HistoryOverview,
  HistorySelection,
  HistorySelectionDetails,
  SelectionDetail,
  SelectionNoticeType,
  SelectionRecord,
  SelectionState,
  SelectionType,
} from 'types/selections'

const basePath = `${baseAPIPathGuruKS}/v1/ks/selections`

export interface ParamsGetActiveSelection {
  regionId?: string
  regionType?: RegionType
  selectionType?: SelectionType
}

export interface ParamsGetHistorySelection extends ParamsGetActiveSelection {
  candidateType?: CandidateType
  page?: number
  pageSize?: number
}

export interface ParamsGetHistorySelectionDetails {
  selectionId: string
  candidateType?: CandidateType
  page?: number
  pageSize?: number
}

export interface ParamsDownloadHistorySelectionDetails {
  selectionId: number
}

export interface ParamsGetHistoryOverview {
  regionId: string
  regionType: RegionType
  candidateType?: CandidateType
}

export interface RequestBodyCreateSelection {
  startDate?: string
  endDate?: string
  regionId?: string
  regionType?: RegionType
  undanganPdf?: string
  undanganDeadline?: string
}

export interface ResponseSuccessCreateSelection {
  id: string
  startDate: string
  endDate: string
  type: string
  regionId: string
  regionType: RegionType
  undanganFileUrl: string
  undanganDeadline: string
  state: SelectionState
}

export interface ResponseSuccessUpdateSelectionState {
  state: SelectionState
}

export interface ParamsGetProfileCandidate {
  selectionId: string
  candidateId: string
}

export interface ResponseSuccessGetHistorySelection {
  data: HistorySelection[]
  meta: {
    page: number
    pageSize: number
  }
}

export interface ResponseSuccessGetHistorySelectionDetails {
  schools: HistorySelectionDetails[]
  meta: {
    page: number
    pageSize: number
    total: number
  }
}

interface ReqBodyCreateSelection {
  endDate: string
  regionId: string
  regionType: RegionType
  undanganPdf: File
  undanganDeadline: string
  noticeType: SelectionNoticeType
  candidateIds: string[]
}

export const useAPICreateSelection = () =>
  useMutationApi<
    ResponseSuccessCreateSelection,
    Error,
    ReqBodyCreateSelection | FormData
  >(basePath, {
    axiosConfig: {
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  })

interface RequestBodyUploadSK {
  skPdf: File
}

export const useAPIUploadSK = (selectionId: string) =>
  useMutationApi<ResponseSuccessCreateSelection, Error, RequestBodyUploadSK>(
    `${basePath}/${selectionId}/surat-keputusan`,
    {
      axiosConfig: {
        method: 'PUT',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

export const useAPIDeleteSK = (selectionId: string) =>
  useMutationApi<ResponseSuccessCreateSelection, Error, FormData>(
    `${basePath}/${selectionId}/surat-keputusan`,
    {
      axiosConfig: {
        method: 'DELETE',
      },
    }
  )

export interface ParamsResetKSSelections {
  regionId: string
  regionType: RegionType
}

// Staging-only: wipes all KS selections data for a region.
// regionId/regionType are sent as query params on the DELETE request.
export const useAPIResetKSSelections = (params: ParamsResetKSSelections) =>
  useMutationApi<{ message?: string }, Error, void>(`${basePath}/reset`, {
    axiosConfig: {
      method: 'DELETE',
      params,
    },
  })

export const useAPIGetActiveSelection = (
  params: ParamsGetActiveSelection = {},
  opts?: UseQueryOptions<any>
) =>
  useQueryApi<ActiveSelection, any>(`${basePath}/active`, {
    axiosConfig: {
      params,
    },
    queryConfig: {
      ...opts,
      retryOnMount: true,
      queryKey: 'getActiveKSSelection',
      enabled: !!params.regionId && !!params.regionType,
    },
  })

export const useAPIGetSelectionDetail = (selectionId: string) =>
  useQueryApi<SelectionDetail, any>(`${basePath}/${selectionId}/details`, {
    queryConfig: {
      staleTime: 1000,
      enabled: !!selectionId,
    },
  })

export interface SelectionCancellationDetails {
  canceledAt: string
  canceledBy: string
  fileName: string
  fileUrl: string
  id: string
  reason: string
  selectionId: string
}

export const useAPIGetSelectionCancellationDetails = (selectionId: string) =>
  useQueryApi<SelectionCancellationDetails, any>(
    `${basePath}/${selectionId}/cancellation-details`,
    {
      queryConfig: {
        enabled: !!selectionId,
      },
    }
  )

export interface SelectionDeadlines {
  invitation: string
  documentSubmission: string
}

export const useAPIGetSelectionDeadlines = (selectionId: string) =>
  useQueryApi<SelectionDeadlines, any>(`${basePath}/${selectionId}/deadlines`, {
    queryConfig: {
      enabled: !!selectionId,
    },
  })

interface RequestBodyExtendSelection {
  selectionEndDate: string
  documentSubmissionEndDate: string
  invitationEndDate: string
  reason: string
}

export const useAPIExtendSelection = (selectionId: string) =>
  useMutationApi<Record<string, unknown>, Error, RequestBodyExtendSelection>(
    `${basePath}/${selectionId}/extend`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
    }
  )

export const useAPIGetHistorySelection = (
  params: ParamsGetHistorySelection = {}
) =>
  useInfiniteQueryApi<ResponseSuccessGetHistorySelection>(
    `${basePath}/finished`,
    {
      axiosConfig: { params },
      queryConfig: {
        queryKey: 'getHistorySelection',
        getNextPageParam: ({ data, meta: { page, pageSize } }) => {
          if (!data.length || data.length < pageSize) return

          return {
            page: page + 1,
            pageSize,
          }
        },
      },
    }
  )

export const useAPIGetHistoryOverview = (params: ParamsGetHistoryOverview) =>
  useQueryApi<HistoryOverview>(
    `${baseAPIPathGuruKS}/v1/${params.candidateType}/selections/finished/overview`,
    {
      axiosConfig: {
        method: 'GET',
        params,
      },
    }
  )

export const useAPIGetHistorySelectionDetails = (
  params: ParamsGetHistorySelectionDetails
) =>
  useInfiniteQueryApi<ResponseSuccessGetHistorySelectionDetails>(
    `${baseAPIPathGuruKS}/v1/${params.candidateType}/selections/${params.selectionId}/pairing/results`,
    {
      axiosConfig: { params },
      queryConfig: {
        getNextPageParam: ({ schools, meta: { page, pageSize } }) => {
          if (!schools.length || schools.length < pageSize) return

          return {
            page: page + 1,
            pageSize,
          }
        },
      },
    }
  )

export const useAPIUpdateSelectionState = (selectionId: string) =>
  useMutationApi<
    ResponseSuccessUpdateSelectionState,
    Error,
    { state: SelectionState }
  >(`${baseAPIPathGuruKS}/v1/ks/selections/${selectionId}/state`, {
    axiosConfig: {
      method: 'PUT',
    },
  })

export const useAPIGetProfileCandidate = (params: ParamsGetProfileCandidate) =>
  useQueryApi<CandidateDetail>(
    `${baseAPIPathGuruKS}/v1/ks/selections/${params.selectionId}/profile/${params.candidateId}`,
    {
      axiosConfig: {
        method: 'GET',
      },
    }
  )

interface RequestBodyUploadBA {
  baPdf: File
}

export const useAPIUploadBA = (selectionId: string) =>
  useMutationApi<{ message: string }, Error, RequestBodyUploadBA>(
    `${basePath}/${selectionId}/berita-acara-pelantikan`,
    {
      axiosConfig: {
        method: 'PUT',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

interface RequestBodyCancelSelection {
  file: File
  reason: string
}

export const useAPICancelSelection = (selectionId: string) =>
  useMutationApi<{ message: string }, Error, RequestBodyCancelSelection>(
    `${basePath}/${selectionId}/cancel`,
    {
      axiosConfig: {
        method: 'PATCH',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

interface ParamsGetSelectionRecords {
  regionId: string
  regionType: RegionType
  recordType:
    | 'UNDEFINED_RECORDS'
    | 'PROPOSAL_RECORDS'
    | 'ADMINISTRATION_RECORDS'
}

export interface ResponseSuccessGetSelectionRecords {
  data: SelectionRecord[]
  meta: {
    lastUpdatedAt: string
  }
}

export const useAPIGetSelectionRecords = (params: ParamsGetSelectionRecords) =>
  useQueryApi<ResponseSuccessGetSelectionRecords>(`${basePath}/records`, {
    axiosConfig: {
      method: 'GET',
      params,
    },
  })

interface RequestBodySelectionsOverride {
  regionId: string
  regionType: RegionType
  npsn: string
  candidateId: string
  skDate: string
  skNumber: string
  taskStartDate: string
  fileSk: File
}

export const useAPISelectionsOverride = () =>
  useMutationApi<{ message: string }, Error, RequestBodySelectionsOverride>(
    `${basePath}/override`,
    {
      axiosConfig: {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

interface RequestBodyCreateAndPairKSRegularSelections {
  regionId: string
  regionType: RegionType
  npsn?: string
  candidateId: string
}

export const useAPICreateAndPairKSRegularSelections = () =>
  useMutationApi<
    { message: string },
    Error,
    RequestBodyCreateAndPairKSRegularSelections
  >(`${basePath}/regular-candidates`, {
    axiosConfig: {
      method: 'POST',
    },
  })

interface RequestBodySelectionsSwasta {
  regionId: string
  regionType: RegionType
  npsn: string
  ptkId: string
  skFileName: string
  taskStartDate: string
  skNumber: string
  skDate: string
  fileSk: File
}

export const useAPISelectionsSwasta = () =>
  useMutationApi<{ message: string }, Error, RequestBodySelectionsSwasta>(
    `${basePath}/swasta`,
    {
      axiosConfig: {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    }
  )

interface RequestBodyFinalizeAdministration {
  regionId: string
  regionType: RegionType
}

export const useAPIFinalizeAdministration = () =>
  useMutationApi<{ message: string }, Error, RequestBodyFinalizeAdministration>(
    `${basePath}/administration/finalization`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )

interface RequestBodyCreateSubstanceSelection {
  regionId: string
  regionType: RegionType
  fileSk: File
}

export const useAPICreateSubstanceSelection = () =>
  useMutationApi<
    { message: string },
    Error,
    RequestBodyCreateSubstanceSelection | FormData
  >(`${basePath}/substance`, {
    axiosConfig: {
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  })

interface RequestBodySendTerminationCandidates {
  includedIds: string[]
  excludedIds: string[]
  regionId: string
  regionType: RegionType
}

export const useAPISendTerminationCandidates = () =>
  useMutationApi<
    { message: string },
    Error,
    RequestBodySendTerminationCandidates
  >(`${basePath}/termination`, {
    axiosConfig: {
      method: 'POST',
    },
  })

interface ParamsKSPostReason {
  regionId: string
  regionType: RegionType
  selectionDemandId: string
  reason: string
}

export const useAPIKSPostReason = () =>
  useMutationApi<{ message: string }, Error, ParamsKSPostReason>(
    ({ selectionDemandId }: ParamsKSPostReason) =>
      `${basePath}/demands/${selectionDemandId}/reasons`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )
